import Enumerable from "linq";
import { AccessFunctionalityTypeEnum } from "../enums/AccessFunctionalityTypeEnum";
import { eLynxObjectTypeEnum } from "../enums/eLynxObjectType";
import { IAccessPermission } from "../models/AccessPermission";
import { IKeyValuePair } from "../models/KeyValuePair";
import { SHA1 } from "crypto-js";
import { AccessTypeEnum } from "../enums/AccessTypeEnum";
import { IObjectSecurityModel } from "../models/IObjectSecurityModel";
import { IGroupSecurityModel } from "../models/IGroupSecurityModel";
import { IPermissionModel } from "../models/IPermissionModel";
import { IPermissionSummaryModel } from "../models/IPermissionSummaryModel";
import { IAccessFunctionalityHashes } from "../models/IAccessFunctionalityHashes";
import i18n from "../../services/localization-service/i18n";
import { IAccessFunctionalityData } from "../models/IAccessFunctionalityData";

export function shapePermissionsData(
    accessDetails: Array<IObjectSecurityModel>,
    accessFunctionalityData: IAccessFunctionalityData,
    accessFunctionalityMasks: Enumerable.IDictionary<eLynxObjectTypeEnum, Enumerable.IDictionary<AccessFunctionalityTypeEnum, number>>,
    accessFunctionalityHashes: Enumerable.IDictionary<eLynxObjectTypeEnum, IAccessFunctionalityHashes>
): Array<IPermissionModel> {

    var objectPermission = Enumerable
        .from(accessDetails)
        .where(accessDetail => !(accessDetail.targetObject.objectType === eLynxObjectTypeEnum.Group && accessDetail.permissions.length === 0))
        .select(accessDetail => {

            return shapePermissionsModel(
                accessDetail,
                accessFunctionalityData,
                accessFunctionalityMasks,
                accessFunctionalityHashes
            );
        });

    return objectPermission.toArray();
}

export function shapePermissionsModel(
    accessDetail: IObjectSecurityModel,
    accessFunctionalityData: IAccessFunctionalityData,
    accessFunctionalityMasks: Enumerable.IDictionary<eLynxObjectTypeEnum, Enumerable.IDictionary<AccessFunctionalityTypeEnum, number>>,
    accessFunctionalityHashes: Enumerable.IDictionary<eLynxObjectTypeEnum, IAccessFunctionalityHashes>
): IPermissionModel {
    let data: IPermissionModel = {
        grantedTo: accessDetail.grantedTo,
        targetObject: accessDetail.targetObject,
        hasDetail: false,
        tatPermissions: undefined,
        permissions: undefined
    };

    var masks: Enumerable.IDictionary<AccessFunctionalityTypeEnum, number> = accessFunctionalityMasks.get(accessDetail.targetObject.objectType);
    var hashes: IAccessFunctionalityHashes = accessFunctionalityHashes.get(accessDetail.targetObject.objectType);

    // have to filter out permissions for functionality types that are not valid.
    // In the legacy app there are cases where all types are set to a value.
    data.permissions = Enumerable
        .from(accessDetail.permissions)
        .select(o => (({
            functionalityType: o.functionalityType,
            access: o.access & masks.get(o.functionalityType)
        }) as IAccessPermission))
        .where(o => o.access > 0)
        .orderBy(o => o.functionalityType)
        .toArray();

    // set the functionality type text for use later
    Enumerable
        .from(data.permissions)
        .join(accessFunctionalityData.functionalityTypes,
            o => o.functionalityType,
            v => v.id,
            (o, v) => o.functionalityTypeText = v.name)
        .toArray();

    let summaryData: IPermissionSummaryModel = getPermissionsSummary(
        data.permissions,
        hashes,
        accessFunctionalityData
    );
    data.summary = summaryData.summary;
    data.summaryDetail = summaryData.summaryDetail;
    data.hasDetail = summaryData.hasDetail;

    if (accessDetail.targetObject.objectType === eLynxObjectTypeEnum.Group) {
        var groupSecurityModel: IGroupSecurityModel = accessDetail as IGroupSecurityModel;

        var tagHashes: IAccessFunctionalityHashes = accessFunctionalityHashes.get(eLynxObjectTypeEnum.Tag);

        data.tatPermissions = Enumerable.from(groupSecurityModel.tatPermissions).select(permission => {

            var summaryData: IPermissionSummaryModel = getPermissionsSummary(
                [{ functionalityType: permission.accessFunctionalityType, access: permission.access }],
                tagHashes,
                accessFunctionalityData
            );

            return {
                tagAssociationTypeID: permission.tagAssociationTypeID,
                tagAssociationTypeName: permission.tagAssociationTypeName ?? "",
                permissions: {
                    functionalityType: permission.accessFunctionalityType,
                    access: permission.access
                } as IAccessPermission,
                summary: summaryData.summary,
                summaryDetail: summaryData.summaryDetail ? summaryData.summaryDetail[0].Value : "",
                hasDetail: summaryData.hasDetail
            };

        }).toArray();
    }

    return data;
}

export function getPermissionsSummary(
    permissions: Array<IAccessPermission>,
    hashes: IAccessFunctionalityHashes,
    accessFunctionalityData: IAccessFunctionalityData
): IPermissionSummaryModel {
    let hash: string = getPermissionHash(permissions);
    let summary: string | undefined = undefined;
    let summaryDetail: Array<IKeyValuePair<string, string>> | undefined = undefined;

    if (hash === hashes.admin) {
        summary = i18n.t("permissionsSummary.admin") as string;
    } else if (hash === hashes.view) {
        summary = i18n.t("permissionsSummary.readOnly") as string;
    } else {
        summary = i18n.t("permissionsSummary.custom") as string;

        var access = Enumerable
            .from(accessFunctionalityData.functionalityTypes).join(
                permissions,
                functionalityType => functionalityType.id,
                permission => permission.functionalityType,
                (functionalityType, permission) => {

                    let accessTypes: Array<string> = Enumerable
                        .from(accessFunctionalityData.accessTypes)
                        .where(at => (at.id & permission.access) > 0)
                        .select(at => at.name).toArray();

                    return {
                        Key: functionalityType.name,
                        Value: accessTypes.join(" | ")
                    } as IKeyValuePair<string, string>;
                })
            .orderBy(a => a.Key)
            .toArray();

        summaryDetail = access;
    }

    return {
        summary: summary,
        summaryDetail: summaryDetail,
        hasDetail: !!summaryDetail && (summaryDetail.length > 0)
    } as IPermissionSummaryModel;
}

export function getPermissionHash(permissions: Array<IAccessPermission>): string {
    //reorder permissions
    let orderedPermissions: Array<IAccessPermission> = Enumerable.from(permissions)
        .select(o => (({
            functionalityType: o.functionalityType,
            access: o.access
        }) as IAccessPermission))
        .orderBy(o => o.functionalityType)
        .toArray();

    //convert all access types to unsigned, for consistancy in permissionHashs (so accessType -1 = 65535), using (x & 0xffff)
    Enumerable.from(orderedPermissions).select(o => {
        o.access &= 0xFFFF;
    }).toArray();

    //stringify permissions
    var permissionString: string = JSON.stringify(orderedPermissions);

    //return hash
    return SHA1(permissionString).toString();
}

export function getAccessPermissions(
    accessFunctionalityData: IAccessFunctionalityData
): Array<{
    objectType: eLynxObjectTypeEnum;
    accessPermissions: Array<IAccessPermission>;
}> {
    return Enumerable
        .from(accessFunctionalityData.accessFunctionalityTypes)
        .groupBy(
            aft => aft.objectType,
            aft => aft,
            (key, group) => {
                return {
                    objectType: key,
                    accessPermissions: group
                        .groupBy(
                            aft => aft.functionalityType,
                            aft => aft,
                            (key, group) => ({
                                functionalityType: key,
                                access: 0xFFFF & group.sum(aft => aft.accessType)
                            })
                        ).toArray()
                };
            }
        )
        .toArray();
}

export function getAccessFunctionalityMasks(
    access: Array<{
        objectType: eLynxObjectTypeEnum;
        accessPermissions: Array<IAccessPermission>;
    }>
): Enumerable.IDictionary<eLynxObjectTypeEnum, Enumerable.IDictionary<AccessFunctionalityTypeEnum, number>> {
    return Enumerable
        .from(access)
        .toDictionary(
            element => element.objectType,
            element => Enumerable
                .from(element.accessPermissions)
                .toDictionary(
                    element => element.functionalityType,
                    element => element.access
                )
        );
}

export function getAccessFunctionalityHashes(
    access: Array<{
        objectType: eLynxObjectTypeEnum;
        accessPermissions: Array<IAccessPermission>;
    }>
): Enumerable.IDictionary<eLynxObjectTypeEnum, IAccessFunctionalityHashes> {
    return Enumerable
        .from(access)
        .toDictionary(
            element => element.objectType,
            element => {
                return {
                    view: getPermissionHash(
                        Enumerable
                            .from(element.accessPermissions)
                            .where(element => (element.access & AccessTypeEnum.Read_View) === AccessTypeEnum.Read_View)
                            .distinct(element => element.functionalityType)
                            .select(ft => (({
                                functionalityType: ft.functionalityType,
                                access: AccessTypeEnum.Read_View
                            }) as IAccessPermission))
                            .toArray()),
                    admin: getPermissionHash(
                        Enumerable
                            .from(element.accessPermissions)
                            .select(ft => (({
                                functionalityType: ft.functionalityType,
                                access: ft.access
                            }) as IAccessPermission))
                            .toArray())
                } as IAccessFunctionalityHashes;
            }
        );
}